import { Link } from "@inertiajs/react";
import { ArrowRight, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { relativeDays } from "@/lib/campaignPlanning";
import { STEP_COPY, trialLabel } from "@/components/schools/setup/steps";
import { ProgressRing } from "@/components/schools/setup/SetupPrimitives";
import { Badge } from "@/components/ui/badge";
import { formatMoney } from "@/lib/format";

/**
 * The slim progress bar across the top of school admin pages until the
 * campaign launches: how far along setup is, the next step, and the
 * countdown to donations opening. The same on every page.
 *
 * On the free trial (banner.trial: campaign not paid for and approved) it
 * says so, with the fee and a "How to pay" link to the payment page.
 * docs/BUSINESS_RULES.md §2a.
 */
export default function SetupBanner({ banner }) {
  const { campaign, progress, next_step: next, key_dates: dates, setup_path: setupPath, ready_to_launch: ready, trial } = banner;
  const withStep = (key) => `${setupPath}${setupPath.includes("?") ? "&" : "?"}step=${key}`;

  let title;
  let detail;
  let href = setupPath;

  if (!campaign) {
    title = "Welcome to PagePledge";
    detail = "The setup guide walks you through everything, in order, from your dates to launch day.";
  } else {
    title = `Setting up ${campaign.name}`;
    // No countdown once the start date has passed (a draft still waiting
    // on required steps), or from a signup draft's placeholder dates.
    const countdown = !campaign.dates_confirmed ? null
      : dates.today < dates.donations_open_on
      ? `Donations open ${relativeDays(dates.today, dates.donations_open_on)}`
      : dates.today < dates.start_on
      ? `Reading starts ${relativeDays(dates.today, dates.start_on)}`
      : null;
    detail = [
      trial?.plan && `${formatMoney(trial.plan.price_cents)} campaign fee to go live`,
      `${progress.complete} of ${progress.total} done`,
      ready ? "Ready for launch" : `Next: ${STEP_COPY[next].title}`,
      countdown,
    ].filter(Boolean).join(" · ");
    href = withStep(next);
  }

  return (
    <div className="mb-8 flex flex-col gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {ready ? (
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
            <Rocket className="h-4 w-4" />
          </span>
        ) : (
          <ProgressRing complete={progress.complete} total={progress.total} size={40} stroke={4} label={false} />
        )}
        <div className="min-w-0">
          <p className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
            <span className="truncate">{title}</span>
            {trial && <Badge variant="warning" className="shrink-0">{trialLabel(trial)}</Badge>}
          </p>
          <p className="text-xs text-muted-foreground">{detail}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3 self-start sm:self-center">
        {trial?.payment_path && (
          <Link href={trial.payment_path} className="text-sm font-medium text-primary hover:underline underline-offset-4">
            How to pay
          </Link>
        )}
        <Button size="sm" asChild>
          <Link href={href}>
            {campaign ? "Continue setup" : "Start setup"}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
